/** Validação e valores padrão da configuração do miolo. */
import { z } from 'zod';
import { anoAtual, diasNoMes, hojeIso, montarIso, partesDe, somarDias } from './calendar/datas.js';
import { MODOS_FERIADO, UFS } from './calendar/feriados.js';
import { hslParaHex } from './color/convert.js';
import { sugerirCorPrincipal } from './color/theory.js';
import { obterTema } from './color/temas.js';
import { criarCadernoPadrao } from './layout/cadernos.js';
import { PAPEIS, SANGRIA_MAXIMA_MM } from './layout/papel.js';
import { camadaDeImagemLegada } from './layout/imagem.js';
import { AJUSTES_IMAGEM, ACABAMENTO_DO_ESTILO_VISUAL, CAIXAS_MOVEIS, CANONS_BIBLIA, CANTOS, ESCOPOS_IMAGEM, ESQUEMAS_HARMONIA, ENCADERNACOES, ESTILOS, ESTILOS_CALIGRAFIA, ESTILOS_ESCRITA, ACABAMENTOS_DA_CAIXA, ESTILOS_VISUAIS, ESPESSURA_MAXIMA_DA_MOLDURA_MM, ESPESSURA_MINIMA_DA_MOLDURA_MM, ESPESSURA_PADRAO_DA_MOLDURA_MM, FAMILIAS_FONTE, FONTES_NOMEADAS, MOLDURAS, FORMATOS, LADOS, LADOS_LOMBADA, LADOS_MOTIVO, MODOS_CALENDARIO, MODOS_DIA, MODOS_MOTIVO, MODOS_RODIZIO, MOTIVOS_LINHA, ORNAMENTOS, ORIENTACOES, PAGINAS_EXTRAS, PAPEIS_IMAGEM, QUADROS_DIA, SEMANAS, TIPOS_CADERNO, TRACOS_LINHA, criarGradeHorariosPadrao, quadrosDesligados, quadrosSugeridos, } from './types/planner.js';
const hex = z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Use uma cor no formato #rrggbb');
export const configCoresSchema = z.object({
    corPrincipal: hex,
    esquema: z.enum(ESQUEMAS_HARMONIA),
    corDestaque: hex.nullable(),
    corTexto: hex.nullable(),
    intensidadeFundo: z.number().min(0).max(1),
    contrasteAlto: z.boolean(),
});
export const configCalendarioSchema = z
    .object({
    modo: z.enum(MODOS_CALENDARIO),
    ano: z.number().int().min(1990).max(2100),
    inicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    fim: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    paginas: z.number().int().min(7).max(800),
    anosParaTras: z.number().int().min(0).max(5).default(0),
    anosParaFrente: z.number().int().min(0).max(5).default(0),
})
    .refine((valor) => valor.modo !== 'DATADO' || valor.inicio <= valor.fim, {
    message: 'A data inicial precisa vir antes da data final.',
    path: ['fim'],
});
export const configCamadaImagemSchema = z.object({
    id: z.string().min(1).max(60),
    url: z.string().min(1),
    nome: z.string().min(1).max(180),
    cor: hex.nullable().optional(),
    papel: z.enum(PAPEIS_IMAGEM),
    lado: z.enum(LADOS),
    canto: z.enum(CANTOS),
    xMm: z.number().min(-400).max(1200),
    yMm: z.number().min(-400).max(1200),
    larguraMm: z.number().min(1).max(2000),
    alturaMm: z.number().min(1).max(2000),
    proporcional: z.boolean(),
    proporcao: z.number().positive().nullable(),
    rotacaoGraus: z.number().min(-360).max(360),
    opacidade: z.number().min(0.02).max(1),
    veu: z.number().min(0).max(0.95),
    desfoque: z.number().min(0).max(24),
    ajuste: z.enum(AJUSTES_IMAGEM),
    escopo: z.enum(ESCOPOS_IMAGEM),
    rodizio: z.enum(MODOS_RODIZIO),
    aCadaNPaginas: z.number().int().min(1).max(400),
    paginas: z.string().max(600),
    espelharPares: z.boolean(),
    ordem: z.number().int().min(0).max(200),
    visivel: z.boolean(),
});
export const imagemCadernoSchema = z.object({
    id: z.string().min(1).max(60),
    url: z.string().min(1),
    nome: z.string().min(1).max(180),
    legenda: z.string().max(200),
    proporcao: z.number().positive().nullable(),
});
export const configCadernoSchema = z.object({
    id: z.string().min(1).max(60),
    tipo: z.enum(TIPOS_CADERNO),
    titulo: z.string().min(1).max(70),
    paginas: z.number().int().min(1).max(200),
    imagens: z.array(imagemCadernoSchema).max(120),
    paginasEmBranco: z.number().int().min(0).max(120),
    estiloPauta: z.enum(ESTILOS_ESCRITA),
    alturaLinhaMm: z.number().min(4).max(14),
    mostrarData: z.boolean(),
    estiloCaligrafia: z.enum(ESTILOS_CALIGRAFIA),
    alturaLinhaCaligrafiaMm: z.number().min(8).max(30),
    canonBiblia: z.enum(CANONS_BIBLIA),
    abreviarLivros: z.boolean(),
    sistemasPorPagina: z.number().int().min(3).max(20),
    itens: z.array(z.string().max(90)).max(80),
    materias: z.array(z.string().max(60)).max(30),
    diasDaGrade: z.array(z.string().max(12)).max(7),
    blocosFolhas: z
        .array(z.object({
        estilo: z.enum(ESTILOS_ESCRITA),
        traco: z.enum(TRACOS_LINHA).optional(),
        paginas: z.number().int().min(1).max(300),
        rotulo: z.string().max(40).optional(),
        alturaLinhaMm: z.number().min(4).max(14).optional(),
    }))
        .max(12)
        .default([]),
    molduraDesenho: z.boolean(),
    visivel: z.boolean(),
});
/**
 * Os quadros opcionais das páginas de dia.
 *
 * A lista sai de `QUADROS_DIA`, e não escrita à mão: a versão anterior era um
 * objeto com os dezessete quadros de então, e quando "Você sabia?" e
 * "Experimente hoje" entraram no produto o schema — que **descarta o que não
 * conhece** — passou a jogar os dois fora no caminho entre a tela e o banco. O
 * miolo salvava sem eles, e o defeito só apareceu no teste que compara o que foi
 * salvo com o que foi pedido.
 */
export const configQuadrosDiaSchema = z.object(Object.fromEntries(QUADROS_DIA.map((quadro) => [quadro, z.boolean()])));
/** Hora no formato `HH:MM`, de 00:00 a 23:59. */
const horaSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use o formato HH:MM, entre 00:00 e 23:59.');
/**
 * Horário de um grupo de dias na grade.
 *
 * O fim antes do início não é recusado aqui: o motor encolhe a faixa para uma
 * hora em vez de deixar o miolo sem abrir.
 */
export const configFaixaDeHorarioSchema = z.object({
    inicio: horaSchema,
    fim: horaSchema,
    livre: z.boolean(),
});
export const configGradeHorariosSchema = z.object({
    segAsex: configFaixaDeHorarioSchema,
    sabado: configFaixaDeHorarioSchema,
    domingo: configFaixaDeHorarioSchema,
    intervaloMinutos: z.union([z.literal(60), z.literal(30)]),
});
export const rotulosQuadrosSchema = z.object(Object.fromEntries(QUADROS_DIA.map((quadro) => [quadro, z.string().max(40).optional()])));
/**
 * Uma caixa posicionada à mão, em milímetros.
 *
 * Os limites são largos de propósito: quem **encaixa** a caixa na folha é o
 * motor, que avisa o que passou. Aqui só se barra o absurdo — config vinda de
 * outro lugar não pode derrubar a montagem.
 */
export const posicaoLivreSchema = z.object({
    xMm: z.number().min(-500).max(500),
    yMm: z.number().min(-500).max(500),
    larguraMm: z.number().min(6).max(500),
    alturaMm: z.number().min(4).max(500),
    /** Camada de desenho: maior fica por cima. Ausente = 0. */
    z: z.number().min(-99).max(99).optional(),
});
export const posicoesLivresSchema = z.object(Object.fromEntries(CAIXAS_MOVEIS.map((chave) => [chave, posicaoLivreSchema.optional()])));
export const dadosPessoaisSchema = z.object({
    nome: z.string().max(90),
    nascimento: z.string().max(20).default(''),
    telefone: z.string().max(40),
    email: z.string().max(120),
    endereco: z.string().max(140).default(''),
    cidade: z.string().max(70),
    cep: z.string().max(12).default(''),
    contatoEmergencia: z.string().max(90),
    relacaoEmergencia: z.string().max(40).default(''),
    telefoneEmergencia: z.string().max(40),
    avisoPerda: z.string().max(400),
    mostrarNaCapa: z.boolean(),
});
export const configPlannerSchema = z.object({
    nome: z.string().min(1, 'Dê um nome para o seu miolo.').max(70),
    formato: z.enum(FORMATOS),
    papel: z
        .object({
        orientacao: z.enum(ORIENTACOES).default('RETRATO'),
        ladoLombada: z.enum(LADOS_LOMBADA).default('ESPELHADA'),
        margemLombadaMm: z.number().min(0).max(40).default(0),
        encadernacao: z.enum(ENCADERNACOES).default('NENHUMA'),
        guiaDeFuro: z.boolean().default(false),
    })
        .default({
        orientacao: 'RETRATO',
        ladoLombada: 'ESPELHADA',
        margemLombadaMm: 0,
        encadernacao: 'NENHUMA',
        guiaDeFuro: false,
    }),
    /**
     * Opcional de propósito: ausente é o mesmo que desligada, e o PDF sai no
     * tamanho exato da folha como sempre saiu.
     */
    sangriaMm: z.number().min(0).max(SANGRIA_MAXIMA_MM).optional(),
    paginasDeDia: z.enum(MODOS_DIA).default('DIA'),
    diasPorPagina: z.union([z.literal(1), z.literal(2)]),
    /**
     * Opcional de propósito: miolo gravado antes deste campo continua válido, e
     * ausente é o mesmo que desligado.
     */
    fimDeSemanaDividido: z.boolean().optional(),
    semana: z.enum(SEMANAS),
    cores: configCoresSchema,
    estilo: z.enum(ESTILOS),
    estiloVisual: z.enum(ESTILOS_VISUAIS).default('CLASSICO'),
    quadrosComCaixa: z.boolean().default(true),
    /*
     * Acabamento do título das caixas do dia.
     *
     * Opcional de propósito (Regra 04): ausente cai em `SIMPLES`, que é o título
     * solto de sempre — miolo salvo antes do recurso abre com o mesmo desenho. O
     * miolo **novo** nasce no acabamento do estilo clássico (filete), que é o que
     * `criarConfigPadrao` monta.
     */
    acabamentoDaCaixa: z.enum(ACABAMENTOS_DA_CAIXA).default('SIMPLES'),
    moldura: z.enum(MOLDURAS).default('NENHUMA'),
    molduraCor: hex.nullable().default(null),
    molduraEspessuraMm: z
        .number()
        .min(ESPESSURA_MINIMA_DA_MOLDURA_MM)
        .max(ESPESSURA_MAXIMA_DA_MOLDURA_MM)
        .default(ESPESSURA_PADRAO_DA_MOLDURA_MM),
    calendario: configCalendarioSchema,
    temaId: z.string().min(1).max(40),
    ornamento: z.enum(ORNAMENTOS),
    tipografia: z.object({
        familia: z.enum(FAMILIAS_FONTE),
        escala: z.number().min(0.8).max(1.25),
        trackingTitulos: z.number().min(0).max(0.4),
        /*
         * Fonte pelo nome (26/09/2026). Ausente no miolo salvo antes disso = a
         * família do sistema, que é o comportamento antigo (Regra 04).
         */
        fonteTitulo: z.enum(FONTES_NOMEADAS).default('SISTEMA'),
        fonteTexto: z.enum(FONTES_NOMEADAS).default('SISTEMA'),
    }),
    escrita: z.object({
        estilo: z.enum(ESTILOS_ESCRITA),
        alturaLinha: z.number().min(4).max(12),
        espacamento: z.number().min(0).max(4),
        comData: z.boolean(),
        traco: z.enum(TRACOS_LINHA).optional(),
        motivo: z.enum(MOTIVOS_LINHA).optional(),
        motivoModo: z.enum(MODOS_MOTIVO).optional(),
        motivoACadaNLinhas: z.number().int().min(1).max(4).optional(),
        motivoEspacamentoMm: z.number().min(3).max(60).optional(),
        motivoTamanhoMm: z.number().min(1.6).max(8).optional(),
        motivoLado: z.enum(LADOS_MOTIVO).optional(),
        corMotivo: hex.nullable().optional(),
    }),
    imagemFundo: z.unknown().optional(),
    camadasImagem: z.array(configCamadaImagemSchema).max(40),
    fundos: z
        .array(z.object({
        id: z.string().min(1).max(60),
        url: z.string().min(1).max(400),
        nome: z.string().max(180).default(''),
        destino: z.enum(ESCOPOS_IMAGEM),
        opacidade: z.number().min(0).max(1).default(1),
        veu: z.number().min(0).max(0.9).default(0),
        ajuste: z.enum(AJUSTES_IMAGEM).default('COBRIR'),
        espelharPares: z.boolean().default(false),
        visivel: z.boolean().default(true),
    }))
        .max(20)
        .default([]),
    /*
     * Sem capa por padrão (27/09/2026).
     *
     * A decisão é do dono do produto, e é de papelaria: quem manda imprimir o miolo
     * faz a capa por fora (capa dura, wire-o com arte própria), então a capa gerada
     * pelo sistema só ocupava uma folha e confundia a encadernação. Ela continua
     * existindo para quem quiser — e volta sozinha quando o cliente envia uma arte
     * de capa (`capas.frente`).
     */
    capa: z.boolean().default(false),
    capas: z
        .object({
        frente: z.string().max(400).nullable().default(null),
        verso: z.string().max(400).nullable().default(null),
        incluirContracapa: z.boolean().default(false),
    })
        .default({ frente: null, verso: null, incluirContracapa: false }),
    cadernos: z.array(configCadernoSchema).max(20),
    feriados: z.enum(MODOS_FERIADO).default('NACIONAIS'),
    uf: z.union([z.enum(UFS), z.literal('')]).default(''),
    mostrarNomesFeriados: z.boolean().default(false),
    quadros: configQuadrosDiaSchema,
    /**
     * Desenho ao lado do título de cada quadro.
     *
     * Opcional de propósito (Regra 04): ausente é o miolo que sempre existiu, sem
     * desenho. O campo **precisa** estar aqui, e não só no tipo: o schema da
     * validação descarta o que não conhece, então um campo que só existe no
     * `ConfigPlanner` some no caminho entre a tela e o banco — foi o que aconteceu
     * com este na primeira versão, e o teste que conta os desenhos na página pegou.
     */
    iconeQuadros: z.boolean().optional(),
    /**
     * Opcional de propósito: miolo salvo antes do recurso abre sem ele, e a grade
     * (desligada por padrão) não aparece.
     */
    gradeHorarios: configGradeHorariosSchema.optional(),
    /**
     * Posição escolhida à mão para as caixas do dia.
     *
     * Opcional de propósito: sem ele o miolo é o de sempre, montado pelo motor
     * (Regra 04). Chaves desconhecidas são descartadas em silêncio — config antiga
     * que cite um quadro que não existe mais não pode invalidar o miolo inteiro.
     */
    posicoesLivres: posicoesLivresSchema.optional(),
    rotulosQuadros: rotulosQuadrosSchema,
    tituloAnotacoes: z.string().max(40),
    dadosPessoais: dadosPessoaisSchema,
    paginasExtras: z.array(z.enum(PAGINAS_EXTRAS)),
    numerarPaginas: z.boolean(),
});
export function validarConfig(valor) {
    const resultado = configPlannerSchema.safeParse(migrarConfig(valor));
    if (resultado.success)
        return { sucesso: true, config: resultado.data, erros: [] };
    return {
        sucesso: false,
        erros: resultado.error.issues.map((problema) => ({
            campo: problema.path.join('.') || 'raiz',
            mensagem: problema.message,
        })),
    };
}
/**
 * Converte configurações antigas (uma única `imagemFundo`) para a lista de
 * camadas. Miolos salvos antes das camadas continuam abrindo normalmente.
 */
function migrarConfig(bruta) {
    if (!bruta || typeof bruta !== 'object')
        return bruta;
    const copia = { ...bruta };
    const formato = copia.formato ?? 'A5';
    /* --- uma única imagem de fundo virou lista de camadas --- */
    if (!Array.isArray(copia.camadasImagem)) {
        const legado = copia.imagemFundo;
        copia.camadasImagem = legado ? [camadaDeImagemLegada(legado, formato, 0)] : [];
    }
    delete copia.imagemFundo;
    /*
     * --- quadros do dia ---
     *
     * Quem **tem** quadros guardados fica com os quadros que tem: miolo salvo não
     * passa por sugestão nenhuma (Regra 04 — dado gravado não se mexe).
     *
     * Quem **não tem** o campo (miolo salvo antes de os quadros existirem, ou um
     * pedido que não os mandou) recebe a sugestão do estilo, que é o que este
     * miolo sempre mostrou. O padrão de fábrica do miolo **novo** — nenhum quadro
     * marcado — mora em `criarConfigPadrao`, e não aqui: mudar este trecho mudaria
     * o que um miolo antigo imprime.
     */
    if (!copia.quadros || typeof copia.quadros !== 'object') {
        const estilo = copia.estilo ?? 'MOTIVACIONAL';
        copia.quadros = quadrosSugeridos(estilo);
    }
    else {
        /**
         * Miolo salvo antes de um quadro novo existir não conhece a chave dele.
         * Completamos com os quadros atuais para o arquivo continuar abrindo — o
         * que o usuário não marcou fica desligado.
         */
        const salvos = copia.quadros;
        copia.quadros = Object.fromEntries(QUADROS_DIA.map((quadro) => [quadro, salvos[quadro] === true]));
    }
    if (!copia.rotulosQuadros || typeof copia.rotulosQuadros !== 'object') {
        copia.rotulosQuadros = {};
    }
    if (typeof copia.tituloAnotacoes !== 'string')
        copia.tituloAnotacoes = '';
    /* --- contatos e notas saíram das páginas extras e viraram cadernos --- */
    const extras = Array.isArray(copia.paginasExtras)
        ? [...copia.paginasExtras]
        : [...PAGINAS_EXTRAS];
    const cadernos = Array.isArray(copia.cadernos) ? [...copia.cadernos] : [];
    if (extras.includes('CONTATOS')) {
        cadernos.unshift(criarCadernoPadrao('CONTATOS', formato));
        extras.splice(extras.indexOf('CONTATOS'), 1);
    }
    if (extras.includes('NOTAS')) {
        const paginas = typeof copia.paginasNotas === 'number' ? copia.paginasNotas : 4;
        if (paginas > 0)
            cadernos.unshift(criarCadernoPadrao('PAUTADO', formato, paginas));
        extras.splice(extras.indexOf('NOTAS'), 1);
    }
    copia.paginasExtras = extras;
    /**
     * Caderno sem páginas reprova na validação e o miolo deixa de abrir. Nos
     * cadernos automáticos (desenho, leitura da Bíblia) o campo é só um rótulo: a
     * contagem real vem do conteúdo, então usamos ela para consertar o valor.
     */
    copia.cadernos = cadernos.map((caderno) => {
        if (!caderno || typeof caderno !== 'object')
            return caderno;
        const item = caderno;
        if (typeof item.paginas === 'number' && item.paginas >= 1)
            return caderno;
        const emBranco = typeof item.paginasEmBranco === 'number' ? item.paginasEmBranco : 0;
        const imagens = Array.isArray(item.imagens) ? item.imagens.length : 0;
        return { ...item, paginas: Math.max(1, emBranco + imagens) };
    });
    delete copia.paginasNotas;
    return copia;
}
/** Configuração inicial sugerida para um novo miolo. */
export function criarConfigPadrao(parcial = {}) {
    const ano = anoAtual();
    const base = {
        nome: `Minha agenda ${ano}`,
        formato: 'A5',
        papel: {
            orientacao: 'RETRATO',
            ladoLombada: 'ESPELHADA',
            margemLombadaMm: 0,
            encadernacao: 'NENHUMA',
            guiaDeFuro: false,
        },
        paginasDeDia: 'DIA',
        diasPorPagina: 1,
        fimDeSemanaDividido: false,
        sangriaMm: 0,
        semana: 'SEG_A_DOM',
        cores: {
            corPrincipal: '#1F4E79',
            esquema: 'AUTO',
            corDestaque: null,
            corTexto: null,
            intensidadeFundo: 0.45,
            contrasteAlto: true,
        },
        estilo: 'MOTIVACIONAL',
        /*
         * O padrão mantém o desenho que já existia: canto arredondado de 3 mm, fundo
         * suave nos quadros e a tipografia que o tema escolheu. Quem quer o outro
         * acabamento escolhe no assistente.
         */
        estiloVisual: 'CLASSICO',
        quadrosComCaixa: true,
        gradeHorarios: criarGradeHorariosPadrao(),
        moldura: 'NENHUMA',
        molduraCor: null,
        molduraEspessuraMm: ESPESSURA_PADRAO_DA_MOLDURA_MM,
        calendario: {
            modo: 'DATADO',
            ano,
            inicio: montarIso(ano, 1, 1),
            fim: montarIso(ano, 12, 31),
            paginas: 180,
            anosParaTras: 0,
            anosParaFrente: 0,
        },
        temaId: 'aurora',
        ornamento: 'LINHA_FINA',
        tipografia: { familia: 'MISTA', escala: 1, trackingTitulos: 0.14 },
        escrita: {
            estilo: 'LINHA',
            alturaLinha: 6,
            espacamento: 0,
            comData: false,
            traco: 'SOLIDA',
            motivo: 'NENHUM',
            motivoModo: 'AO_LONGO_DA_LINHA',
            motivoACadaNLinhas: 1,
            motivoEspacamentoMm: 18,
            motivoTamanhoMm: 3.2,
            motivoLado: 'ESQUERDA',
            corMotivo: null,
        },
        capa: false,
        capas: { frente: null, verso: null, incluirContracapa: false },
        camadasImagem: [],
        fundos: [],
        cadernos: [],
        feriados: 'NACIONAIS',
        uf: '',
        mostrarNomesFeriados: false,
        /*
         * **Nenhum quadro marcado** (decisão de 27/09/2026): o miolo novo nasce com a
         * página do dia limpa, e quem escolhe os quadros é quem usa o assistente.
         * As sugestões por estilo continuam existindo no passo "Quadros do dia",
         * como botão que marca tudo de uma vez.
         */
        quadros: quadrosDesligados(),
        /*
         * O acabamento das caixas do dia no estilo padrão (clássico): um filete sob o
         * título. O campo é opcional (Regra 04) — ausente é o miolo que sempre
         * existiu, com o título solto.
         */
        acabamentoDaCaixa: 'FILETE',
        rotulosQuadros: {},
        tituloAnotacoes: '',
        dadosPessoais: {
            nome: '',
            nascimento: '',
            telefone: '',
            email: '',
            endereco: '',
            cidade: '',
            cep: '',
            contatoEmergencia: '',
            relacaoEmergencia: '',
            telefoneEmergencia: '',
            avisoPerda: '',
            mostrarNaCapa: true,
        },
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
        numerarPaginas: true,
    };
    return {
        ...base,
        ...parcial,
        cores: { ...base.cores, ...(parcial.cores ?? {}) },
        calendario: { ...base.calendario, ...(parcial.calendario ?? {}) },
        tipografia: { ...base.tipografia, ...(parcial.tipografia ?? {}) },
        escrita: { ...base.escrita, ...(parcial.escrita ?? {}) },
        dadosPessoais: { ...base.dadosPessoais, ...(parcial.dadosPessoais ?? {}) },
        capas: { ...base.capas, ...(parcial.capas ?? {}) },
        papel: { ...base.papel, ...(parcial.papel ?? {}) },
        paginasExtras: parcial.paginasExtras ?? base.paginasExtras,
    };
}
/**
 * Aplica um estilo visual sobre a configuração atual.
 *
 * O tema cuida das cores; o estilo cuida do acabamento: tipografia, ornamento e
 * o tratamento dos quadros do dia. Como é só acabamento, ele atravessa qualquer
 * modelo — a mesma agenda fitness sai clássica, moderna ou contemporânea.
 *
 * O contemporâneo desliga as caixas dos quadros (é o "só os dias", sem contorno
 * nem fundo). Voltar para outro estilo liga as caixas de novo, e o interruptor
 * do assistente continua mandando depois disso.
 */
export function aplicarEstiloVisual(config, estilo) {
    const acabamento = ACABAMENTO_DO_ESTILO_VISUAL[estilo];
    return {
        ...config,
        estiloVisual: estilo,
        ornamento: acabamento.ornamento,
        acabamentoDaCaixa: acabamento.acabamentoDaCaixa,
        tipografia: {
            ...config.tipografia,
            familia: acabamento.familia,
            trackingTitulos: acabamento.trackingTitulos,
        },
        quadrosComCaixa: !acabamento.semCaixa,
    };
}
/** Aplica um tema predefinido sobre a configuração atual. */
export function aplicarTema(config, temaId) {
    const tema = obterTema(temaId);
    if (!tema)
        return { ...config, temaId };
    return {
        ...config,
        temaId: tema.id,
        ornamento: tema.ornamento,
        tipografia: {
            ...config.tipografia,
            familia: tema.tipografia,
            escala: tema.tipografia === 'SERIFA' ? 1.02 : 1,
        },
        escrita: {
            ...config.escrita,
            alturaLinha: PAPEIS[config.formato].alturaLinhaPadraoMm,
        },
        cores: {
            ...config.cores,
            corPrincipal: tema.corPrincipal,
            esquema: tema.esquema,
            corDestaque: null,
            intensidadeFundo: tema.intensidadeFundo,
        },
    };
}
/** Ajusta a pauta quando o formato muda, para não desalinhar a escrita. */
export function ajustarParaFormato(config, formato) {
    const alturaLinha = PAPEIS[formato].alturaLinhaPadraoMm;
    return { ...config, formato, escrita: { ...config.escrita, alturaLinha } };
}
/** Períodos prontos usados no passo de calendário. */
export function periodosProntos(ano) {
    return [
        { id: 'ano', rotulo: `Ano ${ano} completo`, inicio: montarIso(ano, 1, 1), fim: montarIso(ano, 12, 31) },
        { id: 'semestre-1', rotulo: `1º semestre ${ano}`, inicio: montarIso(ano, 1, 1), fim: montarIso(ano, 6, 30) },
        { id: 'semestre-2', rotulo: `2º semestre ${ano}`, inicio: montarIso(ano, 7, 1), fim: montarIso(ano, 12, 31) },
        {
            id: 'escolar',
            rotulo: `Ano escolar ${ano}`,
            inicio: montarIso(ano, 2, 1),
            fim: montarIso(ano, 12, 15),
        },
        { id: 'trimestre', rotulo: `Próximos 90 dias`, inicio: hojeIso(), fim: somarDias(hojeIso(), 89) },
    ];
}
/** Sugestões rápidas de cor principal para o botão "surpreenda-me". */
export function sugestoesDeCor(clima = 'natural') {
    return [
        sugerirCorPrincipal(clima),
        sugerirCorPrincipal(clima),
        sugerirCorPrincipal(clima),
        hslParaHex({ h: 205, s: 45, l: 38 }),
        hslParaHex({ h: 14, s: 52, l: 44 }),
        hslParaHex({ h: 152, s: 36, l: 34 }),
    ];
}
/* ------------------------------------------------------------------ */
/* Reaproveitar o miolo no ano seguinte                                */
/* ------------------------------------------------------------------ */
/**
 * Move uma data para outro ano, mantendo mês e dia.
 *
 * Quem começa em 29 de fevereiro cai em 28 no ano seguinte — data que não existe
 * não pode ser inventada.
 */
function deslocarData(iso, novoAno) {
    const { mes, dia } = partesDe(iso);
    return montarIso(novoAno, mes, Math.min(dia, diasNoMes(novoAno, mes)));
}
/**
 * Troca o ano escrito no nome, quando existe; sem ano no nome, acrescenta.
 *
 * O nome é como o cliente identifica a agenda na lista ("Agenda 2026"), então a
 * cópia precisa dizer de que ano ela é. É exportado porque o nome guardado no
 * banco pode ter um sufixo que a configuração não tem ("— A5"), e nesse caso
 * quem desloca o ano é o nome do miolo, não o da configuração.
 */
export function deslocarAnoNoNome(nome, anoAntigo, anoNovo) {
    const antigo = new RegExp(`\\b${anoAntigo}\\b`);
    return antigo.test(nome) ? nome.replace(antigo, String(anoNovo)) : `${nome} ${anoNovo}`;
}
/**
 * Desloca o período de um miolo datado para o próximo ano.
 *
 * É o caminho de quem tem a agenda de 2026 e quer a de 2027 sem remontar tudo:
 * mesmo recorte de calendário, mesmo desenho, ano seguinte. Miolo permanente não
 * tem ano para deslocar e volta como veio — quem chama decide o que fazer com
 * isso.
 */
export function deslocarPeriodoParaProximoAno(config) {
    if (config.calendario.modo !== 'DATADO')
        return config;
    const ano = config.calendario.ano + 1;
    return {
        ...config,
        nome: deslocarAnoNoNome(config.nome, config.calendario.ano, ano),
        calendario: {
            ...config.calendario,
            ano,
            inicio: deslocarData(config.calendario.inicio, ano),
            fim: deslocarData(config.calendario.fim, ano),
        },
    };
}
//# sourceMappingURL=validate.js.map